import React, { useState, useEffect } from 'react';
import './ComingSoonPage.css'; // Keep your own CSS or the one I gave, up to you
import { Loader2, Bell, CheckCircle, Lock, X } from 'lucide-react';
import { supabase } from '../supabaseClient'; 
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';

const ComingSoonPage = ({ 
  title, 
  subtitle, 
  progress, 
  statusText, 
  features,
  toolId // 'home-living' or 'tech-social'
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true); // Start true to check status first
  const [processing, setProcessing] = useState(false); // For button clicks
  const [joined, setJoined] = useState(false);
  const [userSession, setUserSession] = useState(null);

  // 1. CHECK STATUS ON LOAD
  useEffect(() => {
    const checkStatus = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUserSession(user);

      if (user) {
        // Check if row exists for this user + tool
        const { data } = await supabase
          .from('tool_waitlist')
          .select('id')
          .eq('user_id', user.id)
          .eq('tool_category', toolId)
          .maybeSingle(); // Use maybeSingle to avoid errors if empty

        if (data) {
          setJoined(true);
        }
      }
      setLoading(false);
    };

    checkStatus();
  }, [toolId]);

  // 2. HANDLE JOIN (NOTIFY ME)
  const handleNotify = async () => {
    if (!userSession) {
      toast("Please Sign In to join the waitlist.");
      navigate('/login'); 
      return;
    }

    setProcessing(true);

    const { error } = await supabase
      .from('tool_waitlist')
      .insert([
        { 
          user_id: userSession.id, 
          email: userSession.email,
          full_name: userSession.user_metadata?.full_name || '', 
          tool_category: toolId 
        }
      ]);

    if (error) {
      // If code 23505 (Unique Violation), they are already in.
      if (error.code === '23505') {
        setJoined(true);
        toast.success("You are already on the list!");
      } else {
        console.error(error);
        toast.error("Could not join. Please try again.");
      }
    } else {
      setJoined(true);
      toast.success("Added to waitlist!");
    }
    setProcessing(false);
  };

  // 3. HANDLE REMOVE (UNSUBSCRIBE)
  const handleRemove = async () => {
    if (!window.confirm("Remove yourself from this waitlist?")) return;
    
    setProcessing(true);
    
    const { error } = await supabase
        .from('tool_waitlist')
        .delete()
        .eq('user_id', userSession.id)
        .eq('tool_category', toolId);

    if (!error) {
        setJoined(false);
        toast.success("Removed from list.");
    } else {
        toast.error("Failed to remove.");
    }
    setProcessing(false);
  };

  return (
    <div className="coming-soon-wrapper">
      <div className="coming-soon-bg" />
      
      <div className="cs-container">
        {/* Animated Badge */}
        <div className="cs-badge">
          <span className="pulse-dot" />
          {statusText || "Training AI Model..."}
        </div>

        {/* Hero Text */}
        <h1 className="cs-title">{title}</h1>
        <p className="cs-subtitle">{subtitle}</p>

        {/* Progress Bar */}
        <div className="cs-progress-container">
          <div className="progress-label">
            <span>System Readiness</span>
            <span>{progress}%</span>
          </div>
          <div className="progress-track">
            <div className="progress-fill" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {/* --- BUTTON LOGIC --- */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
            
            {loading ? (
                // Initial Load State
                <div style={{ color: '#888', fontSize: '0.9rem' }}>Checking status...</div>
            ) : !joined ? (
                // STATE: NOT JOINED
                <>
                    <button 
                        className="btn btn-primary" 
                        onClick={handleNotify}
                        disabled={processing}
                        style={{ 
                        padding: '14px 35px', borderRadius: '50px', fontWeight: '800',
                        fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '10px',
                        minWidth: '220px', justifyContent: 'center', margin: '0 auto'
                        }}
                    >
                        {processing ? <Loader2 size={20} className="animate-spin" /> : <><Bell size={20} /> Notify Me When Ready</>}
                    </button>
                    {!userSession && (
                        <p style={{fontSize: '0.8rem', color: '#888', display: 'flex', alignItems: 'center', gap: '5px'}}>
                           <Lock size={12}/> Login required
                        </p>
                    )}
                </>
            ) : (
                // STATE: JOINED
                <div className="joined-state">
                    <button 
                        className="btn" 
                        disabled
                        style={{ 
                        padding: '14px 35px', borderRadius: '50px', fontWeight: '800',
                        fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '10px',
                        backgroundColor: '#22c55e', color: 'white', border: 'none',
                        minWidth: '220px', justifyContent: 'center', opacity: 1, margin: '0 auto'
                        }}
                    >
                        <CheckCircle size={20} /> You're on the list!
                    </button>
                    
                    {/* Small Remove Link */}
                    <button 
                        onClick={handleRemove}
                        disabled={processing}
                        style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            fontSize: '0.75rem', color: '#ef4444', textDecoration: 'underline',
                            marginTop: '5px', opacity: 0.8
                        }}
                    >
                        {processing ? "Removing..." : "Remove me from list"}
                    </button>
                </div>
            )}
        </div>

        {/* Features Grid */}
        <div className="cs-features-grid">
          {features.map((f, idx) => (
            <div key={idx} className="cs-feature-card">
              <div className="cs-icon-box">{f.icon}</div>
              <h3>{f.title}</h3>
              <p>{f.description}</p>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
};

export default ComingSoonPage;